import { Box, Stack } from "@mui/material";
import Head from "next/head";
import { GetStaticPaths, GetStaticProps } from "next";
import React from "react";

import {
  ATypeCardView,
  DefaultFacility,
  BTypeBreadCrumb,
  GOI_WEB_URL,
  CTypeTabsFacility,
  ClickEventType,
  useFacilities,
  DTypeFacilityPriceSummary,
  DTypeFacilityList,
  FUNERAL_SIDO,
  CTypeFacilityMainHead,
  DTypeCTA,
  getFacilityTypeLabel,
  getFacilityDisplayName,
  getFacilityRegionKeyword,
} from "goi_common";
import facilities from "goi_common/src/constants/facilities_detail.json";

import { parlorRegion } from "@/constants/parlorRegion";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((facility) => targetSidos.includes(facility.sido))
    .map((facility) => ({
      params: {
        id: String(facility.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id as string;

  const p_facility = facilities.find(
    (facility) => Number(facility.id) === Number(id)
  ) as DefaultFacility;

  const { address, companyname, images, type } = p_facility;
  const typeLabel = getFacilityTypeLabel(type);
  const displayName = getFacilityDisplayName(companyname, type);
  const regionKeyword = getFacilityRegionKeyword(type);

  const 시가아닌_도인경우 = address.split(" ")[0].length == 4;
  const district = 시가아닌_도인경우
    ? address[0] + address[2]
    : address.slice(0, 2);

  const description = `${district} ${displayName}의 비용, 시설정보, 이용조건에 대해서 자세히 알아보세요.`;

  const pageKeywords = `
    ${district} ${regionKeyword},
    ${district} ${regionKeyword} 비용,
    ${district} 봉안당 비용,
    ${district} 장지 비용,
    ${district} 수목장 비용,
    ${companyname},
    ${companyname} 비용,
    ${companyname} 가격,
    ${companyname} 시설비용,
    ${companyname} 안치비용,
    ${companyname} 비용 혜택,
    ${companyname} 안치조건,
    ${companyname} 국가유공자 혜택,
    ${companyname} 기초생활수급자 혜택,
    ${companyname} 공무원 혜택,
    `;

  return {
    props: {
      defaultFacility: p_facility,
      pageDescription: description,
      pageKeywords: pageKeywords,
      pageOgTitle: `(${district}) ${displayName} 비용, 시설 알아보기 | 충청대전세종장례소개포털`,
      pageOgDescription: description,
      pageOgImage: images[0]?.url || "",
    },
  };
};

interface PriceProps {
  defaultFacility: DefaultFacility;
  pageOgTitle: string;
}

export default function FacilityPrice({
  defaultFacility,
  pageOgTitle,
}: PriceProps) {
  const currentPageEvent: ClickEventType = {
    domain: "ch",
    path: "facility_price",
  };

  const { companyname, id, type } = defaultFacility;
  const displayName = getFacilityDisplayName(companyname, type);

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        {/* {regionParlors.length > 5 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(carouselRichSearch),
          }}
        />
      )} */}
      </Head>

      <BTypeBreadCrumb
        firstItem={{
          text: `장지 추천`,
          href: `${GOI_WEB_URL}/match/facility-requests/${id}/?utm_source=dogsound`,
          event: {
            ...currentPageEvent,
            trigger_and_target: "br_crumb_mth_facil_req",
          },
        }}
        // secondItem={{
        //   text: `${sidoName} 장례식장`,
        //   href: `/parlors-list/${sidoName} 의 장례식장 목록/`,
        //   event: {
        //     domain: "ch",
        //     path: "prlr_item_intro",
        //     trigger_and_target: "br_crumb_sido_prlr_list",
        //   },
        // }}
        // thirdItem={{
        //   text: `${sigunguName} 장례식장`,
        //   href: `/parlors-list/${sidoName}-${sigunguName} 의 장례식장 목록/`,
        //   event: {
        //     domain: "ch",
        //     path: "prlr_item_intro",
        //     trigger_and_target: "br_crumb_sigungu_prlr_list",
        //   },
        // }}
        goiHomeEvent={{
          ...currentPageEvent,
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <CTypeFacilityMainHead
        title={`${displayName} 비용`}
        defaultFacility={defaultFacility}
        mainColor={regionColor}
        event={{
          ...currentPageEvent,
          trigger_and_target: "fac_goi_call",
        }}
      />
      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
        <DTypeCTA
          mainColor={regionColor}
          companyname={companyname}
          href={`${GOI_WEB_URL}/match/facility-requests/${id}/?utm_source=dogsound`}
          text={`${companyname} 상세 비용 알아보기`}
          event={{
            ...currentPageEvent,
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      </Stack>

      <ParlorPriceContentSection
        defaultFacility={defaultFacility}
        event={currentPageEvent}
      />
    </>
  );
}

interface ParlorPriceContentSectionProps {
  defaultFacility: DefaultFacility;
  event: ClickEventType;
}

const ParlorPriceContentSection = ({
  defaultFacility,
  event,
}: ParlorPriceContentSectionProps) => {
  const { facilityList, facilityPrices } = useFacilities({
    sido: defaultFacility.sido,
  });

  const sidoName = FUNERAL_SIDO.get(Number(defaultFacility.sido));

  return (
    <>
      <CTypeTabsFacility
        href={`facility-item/${defaultFacility.id}`}
        mainColor={regionColor}
        selectTab="비용"
        event={{ ...event, trigger_and_target: "CType_facil_tab" }}
      />

      <Stack textAlign="center" padding="20px 34px">
        <DTypeFacilityPriceSummary
          defaultFacility={defaultFacility}
          mainColor={regionColor}
        />
      </Stack>

      <Box marginTop="40px" />

      <DTypeFacilityList
        title={`${sidoName} 지역의 다른 장지`}
        mainColor={regionColor}
      >
        <Stack component="ul" gap="16px">
          {facilityList.length > 0 &&
            facilityList?.map((res, index) => {
              const { max, min } = facilityPrices[index];
              const averagePrice = (max + min) / 2;

              return (
                <ATypeCardView
                  key={res.companyname}
                  name={res.companyname}
                  href={`/facility-item/${res.id}/price/`}
                  arrowText={`${getFacilityDisplayName(res.companyname, res.type)} 비용 알아보기`}
                  descriptions={[
                    `장지 평균 비용 : ${averagePrice.toLocaleString()}원`,
                  ]}
                  imgSrc={res.images?.[0].url || ""}
                  event={{
                    ...event,
                    trigger_and_target: "uv_facility_price",
                  }}
                />
              );
            })}
        </Stack>
      </DTypeFacilityList>
    </>
  );
};
